"use client";

import React from "react";
import { SlidersHorizontal as Sliders, Sparkles, Grid3x3 as Grid3X3, ShieldCheck, Wind, ArrowRight, Check } from 'lucide-react';
import { motion, useReducedMotion } from "framer-motion";
import { Link } from "@/components/common/Link";
import { Badge } from "@/components/ui/badge";
import { Button } from "@/components/ui/button";
import { Card } from "@/components/ui/card";
import { Separator } from "@/components/ui/separator";
const scopeItems = [{
  id: 1,
  title: "Layout planning & spatial design",
  description: "Architectural drawings, wall reconfiguration, and plumbing rerouting tailored to your Decatur home's exact footprint.",
  icon: "sliders"
}, {
  id: 2,
  title: "Designer fixtures & solid brassware",
  description: "Thermostatic shower valves, rainfall heads, undermount vitreous china sinks, and precision-finished architectural hardware.",
  icon: "sparkles"
}, {
  id: 3,
  title: "Artisanal tile & radiant heat",
  description: "Hand-laid porcelain or natural stone with Schluter-DITRA waterproof uncoupling and programmable underfloor warming systems.",
  icon: "grid"
}, {
  id: 4,
  title: "Zero-threshold curbless showers",
  description: "Linear drainage channels, structural slope engineering, frameless heavy glass enclosures, and seamless aging-in-place entry.",
  icon: "shield"
}, {
  id: 5,
  title: "High-efficiency moisture control",
  description: "Whisper-quiet Panasonic sensor exhaust fans, antimicrobial vapor barriers, and mold-resistant cementitious substrate assemblies.",
  icon: "wind"
}];
const showcaseImages = [{
  id: 1,
  title: "Walk-in shower with bespoke copper trim",
  subtitle: "Custom Walk-In Suite",
  src: "https://wpvc-images.s3.us-east-1.amazonaws.com/images/1821937/img/bathroom_walk_in_shower_copper.png",
  srcset: "https://wpvc-images.s3.us-east-1.amazonaws.com/images/1821937/img/bathroom_walk_in_shower_copper.png-wvc-srcset",
  sizes: "https://wpvc-images.s3.us-east-1.amazonaws.com/images/1821937/img/bathroom_walk_in_shower_copper.png-wvc-sizes",
  alt: "Modern walk-in curbless shower with artisanal tilework and warm copper fixtures"
}, {
  id: 2,
  title: "Custom white oak double vanity",
  subtitle: "Handcrafted Cabinetry",
  src: "https://wpvc-images.s3.us-east-1.amazonaws.com/images/1821937/img/bathroom_double_vanity_quartz.png",
  srcset: "https://wpvc-images.s3.us-east-1.amazonaws.com/images/1821937/img/bathroom_double_vanity_quartz.png-wvc-srcset",
  sizes: "https://wpvc-images.s3.us-east-1.amazonaws.com/images/1821937/img/bathroom_double_vanity_quartz.png-wvc-sizes",
  alt: "Custom oak double vanity with quartz countertops and undermount sinks"
}];
export default function Included() {
  const shouldReduceMotion = useReducedMotion();
  const getIcon = (iconName: string) => {
    switch (iconName) {
      case "sliders":
        return <Sliders className="size-5 text-chart-1" />;
      case "sparkles":
        return <Sparkles className="size-5 text-chart-1" />;
      case "grid":
        return <Grid3X3 className="size-5 text-chart-1" />;
      case "shield":
        return <ShieldCheck className="size-5 text-chart-1" />;
      case "wind":
        return <Wind className="size-5 text-chart-1" />;
      default:
        return <Check className="size-5 text-chart-1" />;
    }
  };
  const containerVariants = {
    hidden: {
      opacity: shouldReduceMotion ? 1 : 0
    },
    visible: {
      opacity: 1,
      transition: {
        staggerChildren: shouldReduceMotion ? 0 : 0.08
      }
    }
  };
  const itemVariants = {
    hidden: {
      opacity: shouldReduceMotion ? 1 : 0,
      y: shouldReduceMotion ? 0 : 16
    },
    visible: {
      opacity: 1,
      y: 0,
      transition: {
        duration: 0.4,
        ease: [0.16, 1, 0.3, 1]
      }
    }
  };
  return <section id="whats-included" data-nav="light" className="relative bg-background text-foreground py-16 sm:py-20 lg:py-24 border-b border-border">
      <div className="mx-auto max-w-7xl px-4 sm:px-6 lg:px-8">
        {/* Header Block */}
        <motion.div initial={{
        opacity: shouldReduceMotion ? 1 : 0,
        y: shouldReduceMotion ? 0 : 16
      }} whileInView={{
        opacity: 1,
        y: 0
      }} viewport={{
        once: true,
        amount: 0.3
      }} transition={{
        duration: 0.4,
        ease: [0.16, 1, 0.3, 1]
      }} className="max-w-3xl mb-12 sm:mb-16">
          <div className="inline-flex items-center gap-2 mb-3">
            <Badge variant="secondary" className="bg-secondary text-foreground text-xs font-semibold uppercase tracking-wider px-3 py-1 border border-border">
              Full-Service Remodel Scope
            </Badge>
          </div>

          <h2 className="font-serif text-3xl sm:text-4xl lg:text-5xl font-semibold tracking-tight text-foreground leading-tight mb-4">
            Everything built into your bathroom renovation.
          </h2>

          <p className="font-sans text-base sm:text-lg text-muted-foreground leading-relaxed">
            From precision curbless waterproofing to radiant heated floors and handcrafted cabinetry, every single element is engineered for daily comfort, timeless aesthetics, and long-term durability in Decatur homes.
          </p>
        </motion.div>

        {/* Asymmetrical Content Grid */}
        <div className="grid grid-cols-1 lg:grid-cols-12 gap-8 lg:gap-10 items-start">
          {/* Left Column: Feature Scope Cards */}
          <motion.div variants={containerVariants} initial="hidden" whileInView="visible" viewport={{
          once: true,
          amount: 0.2
        }} className="lg:col-span-7 flex flex-col gap-4">
            {scopeItems.map((item, i) => <motion.div key={item.id} data-index={i} variants={itemVariants} whileHover={shouldReduceMotion ? undefined : {
            y: -4
          }} className="transition-[border-color,box-shadow] duration-300">
                <Card className="p-5 sm:p-6 bg-card border border-border rounded-xl shadow-xs hover:border-chart-1/40 hover:shadow-md">
                  <div className="flex items-start gap-4 sm:gap-5">
                    <div className="shrink-0 size-11 rounded-lg bg-secondary flex items-center justify-center border border-border">
                      {getIcon(item.icon)}
                    </div>
                    <div className="flex-1">
                      <div className="flex items-center justify-between gap-2 mb-1.5">
                        <h3 className="font-serif text-lg sm:text-xl font-semibold text-foreground">
                          {item.title}
                        </h3>
                        <span className="font-mono text-xs text-muted-foreground shrink-0 font-medium">
                          0{item.id}
                        </span>
                      </div>
                      <p className="font-sans text-sm sm:text-base text-muted-foreground leading-relaxed">
                        {item.description}
                      </p>
                    </div>
                  </div>
                </Card>
              </motion.div>)}

            {/* Bottom Support Info Box */}
            <motion.div variants={itemVariants} className="mt-2 p-5 sm:p-6 rounded-xl bg-secondary/70 border border-border flex flex-col sm:flex-row items-start sm:items-center justify-between gap-4">
              <div>
                <p className="font-serif font-semibold text-foreground text-base">
                  Need a custom structural expansion or relocation?
                </p>
                <p className="font-sans text-sm text-muted-foreground mt-0.5">
                  We handle all permits, plumbing re-routing, and structural framing.
                </p>
              </div>
              <Button asChild className="bg-primary text-primary-foreground hover:bg-primary/90 font-medium text-sm shrink-0">
                <Link to="#" className="inline-flex items-center gap-1.5">
                  <span>Schedule Consultation</span>
                  <ArrowRight className="size-4" />
                </Link>
              </Button>
            </motion.div>
          </motion.div>

          {/* Right Column: Photography Showcase */}
          <motion.div initial={{
          opacity: shouldReduceMotion ? 1 : 0,
          y: shouldReduceMotion ? 0 : 20
        }} whileInView={{
          opacity: 1,
          y: 0
        }} viewport={{
          once: true,
          amount: 0.2
        }} transition={{
          duration: 0.5,
          ease: [0.16, 1, 0.3, 1],
          delay: 0.15
        }} className="lg:col-span-5 flex flex-col gap-6 lg:sticky lg:top-24">
            {showcaseImages.map((image, i) => <div key={image.id} data-index={i} className="group relative overflow-hidden rounded-xl border border-border bg-card shadow-sm transition-[border-color,box-shadow] duration-300 hover:border-chart-1/30 hover:shadow-md">
                <div className="relative aspect-4/3 w-full overflow-hidden bg-muted">
                  <img src={image.src} data-wvc-srcset={image.srcset} data-wvc-sizes={image.sizes} alt={image.alt} loading="lazy" decoding="async" className="h-full w-full object-cover transition-transform duration-500 group-hover:scale-103" />
                  <div className="absolute inset-0 bg-gradient-to-t from-black/60 via-black/10 to-transparent pointer-events-none" />

                  {/* Frosted Layer Caption Plate */}
                  <div className="absolute bottom-3 inset-x-3 p-3.5 rounded-lg bg-card/90 backdrop-blur-md border border-border text-card-foreground shadow-xs">
                    <p className="font-sans text-xs uppercase tracking-wider font-semibold text-chart-1">
                      {image.subtitle}
                    </p>
                    <p className="font-serif text-sm sm:text-base font-semibold text-foreground mt-0.5">
                      {image.title}
                    </p>
                  </div>
                </div>
              </div>)}

            {/* Quality Commitment Callout */}
            <div className="p-5 rounded-xl border border-border bg-card flex items-center gap-4">
              <div className="shrink-0 size-10 rounded-full bg-secondary flex items-center justify-center border border-border">
                <ShieldCheck className="size-5 text-chart-1" />
              </div>
              <div className="text-xs sm:text-sm">
                <span className="font-semibold text-foreground block">
                  Finished with careful attention to detail
                </span>
                <span className="text-muted-foreground">
                  Decatur, Oakhurst, Winnona Park & surrounding neighborhoods.
                </span>
              </div>
            </div>
          </motion.div>
        </div>

        <Separator className="mt-16 sm:mt-20 bg-border" />
      </div>
    </section>;
}